import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import '../scss/components/BottomNav.scss';
import { authService } from '../services/authService';
import { FaCrown } from 'react-icons/fa';
import '../scss/upgradeBar.scss';
import ProFeatureModal from './ProFeatureModal';
import api from '../services/apiService';

function BottomNav() {
  const location = useLocation();
  const [showProModal, setShowProModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeBar, setShowUpgradeBar] = useState(false);
  
  // Don't show on login/register pages or team selection page
  const hideNavPaths = [
    '/', 
    '/login', 
    '/register',
    '/register-team-page',
    '/register-parent',
    '/forgot-password',
    '/team-selection',
    '/team-selection/'
  ];
  
  // Check if current path starts with any of the team selection paths
  const isTeamSelectionPath = location.pathname.startsWith('/team-selection');
  
  // Don't show if path is in hideNavPaths or starts with team-selection
  const shouldHideNav = hideNavPaths.includes(location.pathname) || isTeamSelectionPath;
  
  // Use the authService for consistent manager status checking
  const isManager = authService.isManager();
  const managerSessionValue = sessionStorage.getItem('is_manager');
  const userType = sessionStorage.getItem('user_type');
  const isManagerByUserType = userType === 'manager' || userType === 'coach';
  const hasTraining = sessionStorage.getItem('selected_team_has_training') === 'true' || sessionStorage.getItem('selected_team_has_training') === '1';
  
  // Use either method to determine manager status
  const isActuallyManager = isManager || isManagerByUserType;
  
  // Fetch subscription status for upgrade bar
  const fetchSubscriptionStatus = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const teamId = sessionStorage.getItem('selected_team_id');
      
      // Build URL with team_id parameter if available
      let apiUrl = `${process.env.REACT_APP_API_URL}/get_subscription_status.php`;
      if (teamId) {
        apiUrl += `?team_id=${teamId}`;
      }
      
      const response = await api.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setSubscriptionStatus(response.data);
        const isPro = authService.isPro();
        const isManager = authService.isManager();
        const isCoach = sessionStorage.getItem('user_type') === 'coach';
        
        // Use API response data for Pro status instead of authService.isPro() for more accurate results
        const isProFromAPI = response.data.is_pro === true || response.data.has_active_subscription === true;
        
        // For academy children with Pro parents, don't show trial bar even if technically in trial period
        const isChild = sessionStorage.getItem('is_child') === 'true';
        const teamType = sessionStorage.getItem('selected_team_type');
        const isAcademyChildWithPro = isChild && teamType === 'academy' && isProFromAPI;
        
        // Show upgrade bar if user is in trial (but not if academy child with Pro parent) OR if they're not Pro/Manager/Coach
        const shouldShowForTrial = response.data.in_trial === true && response.data.trial_days_remaining > 0 && !isAcademyChildWithPro;
        const shouldShowForNonPro = !isProFromAPI && !isManager && !isCoach;
        setShowUpgradeBar(shouldShowForTrial || shouldShowForNonPro);
        

      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };
  
  useEffect(() => {
    if (authService.isAuthenticated()) {
      fetchSubscriptionStatus();
    }

    // Listen for token refresh events (after payment completion)
    const handleTokenRefresh = () => {
      console.log('Token refreshed, re-fetching subscription status');
      fetchSubscriptionStatus();
    };

    // Listen for storage changes (when pro_payment_completed is set)
    const handleStorageChange = (e) => {
      if (e.key === 'pro_payment_completed' && e.newValue === 'true') {
        console.log('Payment completed, re-fetching subscription status');
        setTimeout(() => fetchSubscriptionStatus(), 1000); // Small delay to ensure token is refreshed
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events
    window.addEventListener('token-refreshed', handleTokenRefresh);
    window.addEventListener('payment-completed', handleTokenRefresh);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('token-refreshed', handleTokenRefresh);
      window.removeEventListener('payment-completed', handleTokenRefresh);
    };
  }, [userType, location, shouldHideNav, isManager]);

  const handleUpgradeClick = () => {
    setShowProModal(true);
  };



  if (shouldHideNav) {
    return null;
  }

  // Show trial bar if user is in trial and has remaining days
  const isInTrial = subscriptionStatus?.in_trial === true && subscriptionStatus?.trial_days_remaining > 0;
  const isExpired = !isInTrial;



  return (
    <>
      <div className="upgradeBar-nav-container">
        {showUpgradeBar && subscriptionStatus && (
          <div className={`upgrade-bar ${isExpired ? 'expired' : ''}`}>
            <div className="upgrade-bar-content">
              <div className="trial-info">
                <FaCrown className="crown-icon" />
                <span>
                  {isExpired 
                    ? 'Your trial has expired!'
                    : `${subscriptionStatus.trial_days_remaining} days left in your trial`
                  }
                </span>
              </div>
              <button className="upgrade-button" onClick={handleUpgradeClick}>
                {isExpired ? 'Upgrade Now' : 'Upgrade to Pro'}
              </button>
            </div>
          </div>
        )}
        
        <nav className={`bottom-nav ${showUpgradeBar ? 'with-upgrade-bar' : ''}`}>
          <ul>
            <li>
              <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'active' : '')}>
                <i className="fas fa-home"></i>
                <span>Home</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/matches" className={({ isActive }) => (isActive ? 'active' : '')}>
                <i className="fas fa-futbol"></i>
                <span>Fixtures</span>
              </NavLink>
            </li>
            {/* Show Training tab only for managers when training is active */}
            {isActuallyManager && hasTraining && (
              <li>
                <NavLink to="/trainingSchedule" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <i className="fas fa-running"></i>
                  <span>Training</span>
                </NavLink>
              </li>
            )}

            {/* Show Players tab for coaches and managers */}
            {isActuallyManager && (
              <li>
                <NavLink to="/players" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <i className="fas fa-users"></i>
                  <span>Players</span>
                </NavLink>
              </li>
            )}

            {/* Hide Stats link if user is a manager */}
            {!isActuallyManager && (
              <li>
                <NavLink to="/leaderboard" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <i className="fas fa-trophy"></i>
                  <span>Stats</span>
                </NavLink>
              </li>
            )}
            {/* Show Resources link if user is a parent */}
            {userType === 'parent' && (
              <li>
                <NavLink to="/parent/resources" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <i className="fas fa-book-open"></i>
                  <span>Resources</span>
                </NavLink>
              </li>
            )}
            {/* Hide Profile link if user is a manager */}
            {!isActuallyManager && (
              <li>
                <NavLink to="/profile" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <i className="fas fa-user"></i>
                  <span>Profile</span>
                </NavLink>
              </li>
            )}
            {authService.isAdmin() && (
              <li>
                <NavLink to="/admin" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <i className="fas fa-cog"></i>
                  <span>Admin</span>
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
      </div>
      
      {showProModal && (
        <ProFeatureModal
          isOpen={showProModal}
          onClose={() => setShowProModal(false)}
          featureName="Pro Features"
        />
      )}
    </>
  );
}

export default BottomNav; 